import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
  checkGuidance,
  GENERATION_CANCELLED_MESSAGE,
  GUIDANCE_MAX,
  RIGOR_LABELS,
  RIGOR_LEVELS,
  type Rigor,
} from "@book2class/shared";
import { api, ApiError } from "../api";
import { Spinner } from "../components/ui";
import { money, useFx } from "../fx";

type Estimate = {
  ebookTokens: number;
  realtimeUsd: number;
  batchUsd: number;
  narration?: { enabled: boolean; voice: string; chars: number; listUsd: number; freeChars: number };
  duration?: { minutes: number; basedOn: number; pages: number } | null;
  rigor?: number;
  guidance?: string;
  byTask: Array<{ task: string; provider: string | null; model: string | null; priced: boolean }>;
};

type Generation = {
  course: { id: string; title: string; status: string };
  version?: { id: string; number: number };
  generation: null | {
    status: "queued" | "running" | "waiting_batch" | "succeeded" | "failed";
    mode?: "realtime" | "batch";
    error?: string | null;
    startedAt?: string;
    finishedAt?: string;
    attempts: number;
    maxAttempts: number;
    autoResumes?: number;
    slow?: boolean;
    timings?: Record<string, { startedAt: string; finishedAt?: string; activeMs: number }>;
    activeMs?: number;
  };
  current?: { step?: string; done?: number; total?: number } | null;
  steps?: Array<{ id: string; label: string; status: string }>;
  usage?: { costUsd: number; inputTokens: number; outputTokens: number; calls: number };
  counts?: Record<string, number>;
};

/** Explicação simples do motivo da parada, com a solução sugerida. */
export function explainGenerationStop(error?: string | null) {
  const text = error ?? "";
  if (text === GENERATION_CANCELLED_MESSAGE) return "Você interrompeu a geração.";
  if (/formato esperado|StructuredOutput/i.test(text))
    return "O modelo de IA teve dificuldade em organizar parte do conteúdo no formato do curso, mesmo depois de tentar com outro modelo.";
  if (/429|limite de requisi|rate limit|quota/i.test(text))
    return "O provedor de IA atingiu o limite de uso por minuto ou por dia do plano atual.";
  if (/chave|api key|não configurada/i.test(text))
    return "A chave de acesso ao provedor de IA está ausente ou inválida.";
  if (/teto|orçamento|budget/i.test(text))
    return "O teto de gastos com IA definido para a instituição foi atingido.";
  return "O provedor de IA ficou indisponível por um tempo maior que o normal.";
}

/** A geração segue ativa, mas já precisou de tentativas extras ou de outro modelo. */
export function isSlowGeneration(g: { slow?: boolean; error?: string | null }) {
  // O aviso vale enquanto a etapa com dificuldade não avança; o servidor o limpa quando a geração segue.
  return Boolean(g.slow || g.error);
}

type Outline = {
  modules: Array<{
    id: string;
    position: number;
    title: string;
    summary: string | null;
    lessons: Array<{
      id: string;
      position: number;
      title: string;
      readingMinutes: number | null;
      sourcePageStart: number | null;
      sourcePageEnd: number | null;
    }>;
  }>;
};

type SearchResult = {
  id: string;
  kind: string;
  title: string;
  context?: string;
  snippet: string;
  pageStart?: number | null;
  pageEnd?: number | null;
};

const int = (n: number) => n.toLocaleString("pt-BR");
/** Duração legível: segundos, minutos ou horas. */
const span = (ms: number) =>
  ms >= 3_600_000
    ? `${Math.floor(ms / 3_600_000)} h ${Math.round((ms % 3_600_000) / 60_000)} min`
    : ms >= 60_000
      ? `${Math.round(ms / 60_000)} min`
      : `${Math.max(1, Math.round(ms / 1000))} s`;
const clock = (iso?: string) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—";
const ACTIVE = new Set(["queued", "running", "waiting_batch"]);

// ─────────────────────────── Início da geração ───────────────────────────

/** Cartão exibido na página do ebook: estimativa de custo, modo de geração e início. */
export function GenerateCard({ courseId }: { courseId: string }) {
  const navigate = useNavigate();
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [gen, setGen] = useState<Generation | null>(null);
  const [mode, setMode] = useState<"realtime" | "batch">("realtime");
  const [narrate, setNarrate] = useState(false);
  const [rigor, setRigor] = useState<Rigor>(3);
  const [guidance, setGuidance] = useState("");
  const fx = useFx();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get<Estimate>(`/courses/${courseId}/estimate`)
      .then((e) => {
        setEstimate(e);
        if (e.rigor) setRigor(e.rigor as Rigor);
        if (e.guidance) setGuidance(e.guidance);
      })
      .catch(() => undefined);
    api
      .get<Generation>(`/courses/${courseId}/generation`)
      .then(setGen)
      .catch(() => undefined);
  }, [courseId]);

  const status = gen?.generation?.status;
  if (status && status !== "failed")
    return (
      <div className="card card-pad gen-cta">
        <div>
          <h2 style={{ fontSize: "1.15rem", marginBottom: 4 }}>
            {ACTIVE.has(status) ? "Geração do curso em andamento" : "Curso gerado"}
          </h2>
          <p className="segment-meta" style={{ margin: 0 }}>
            {ACTIVE.has(status)
              ? "Acompanhe as etapas e o custo em tempo real."
              : "Confira o sumário e o que foi gerado."}
          </p>
        </div>
        <Link
          className="btn"
          to={ACTIVE.has(status) ? `/professor/cursos/${courseId}/geracao` : `/professor/cursos/${courseId}`}
        >
          {ACTIVE.has(status) ? "Acompanhar" : "Revisar e publicar"}
        </Link>
      </div>
    );

  const guidanceCheck = checkGuidance(guidance);
  const simulated = estimate?.byTask.every((t) => t.provider === "simulator");
  const unpriced = estimate?.byTask.some((t) => t.provider !== "simulator" && !t.priced);

  async function start(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post(`/courses/${courseId}/generate`, { mode, narrate, rigor, guidance });
      navigate(`/professor/cursos/${courseId}/geracao`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Não foi possível iniciar a geração.");
      setBusy(false);
    }
  }

  return (
    <form className="card card-pad gen-start" onSubmit={start}>
      <div className="eyebrow">Próxima etapa</div>
      <h2 style={{ fontSize: "1.25rem" }}>
        {status === "failed" ? "Retomar a geração do curso" : "Gerar o curso com IA"}
      </h2>
      <p style={{ color: "var(--ink-2)" }}>
        A IA propõe módulos e aulas a partir destes segmentos e cria quizzes, flashcards, estudos de caso,
        exercícios, glossário e avaliação final. Tudo passa pela sua revisão antes da publicação.
      </p>
      {status === "failed" && (
        <p className="alert">
          A geração anterior ficou incompleta. {explainGenerationStop(gen?.generation?.error)} Ao retomar, o
          que já foi gerado é mantido e a IA continua de onde parou.
        </p>
      )}
      <div className="radio-cards gen-modes" role="radiogroup" aria-label="Modo de geração">
        <label className="radio-card">
          <input
            type="radio"
            name="mode"
            checked={mode === "realtime"}
            onChange={() => setMode("realtime")}
          />
          <span>
            <strong>Agora</strong> ·{" "}
            {estimate?.duration
              ? `duração estimada de ${span(estimate.duration.minutes * 60_000)}`
              : "começa imediatamente"}
            <br />
            <span className="segment-meta">
              Custo estimado: {estimate ? money(estimate.realtimeUsd, fx) : "…"}
            </span>
          </span>
        </label>
        <label className="radio-card">
          <input type="radio" name="mode" checked={mode === "batch"} onChange={() => setMode("batch")} />
          <span>
            <strong>Em lote</strong> · mais barato, pode levar algumas horas
            <br />
            <span className="segment-meta">
              Custo estimado: {estimate ? money(estimate.batchUsd, fx) : "…"}
            </span>
          </span>
        </label>
      </div>
      {estimate?.narration?.enabled && (
        <label className="check" style={{ marginTop: 12 }}>
          <input type="checkbox" checked={narrate} onChange={(e) => setNarrate(e.target.checked)} />
          <span>
            Gerar a narração das aulas com voz neural ao final da geração
            <br />
            <span className="segment-meta">
              Cerca de {int(estimate.narration.chars)} caracteres · preço cheio{" "}
              {money(estimate.narration.listUsd, fx)}
              {estimate.narration.chars <= estimate.narration.freeChars
                ? ` · dentro da cota gratuita de ${int(estimate.narration.freeChars)} caracteres por mês do Google, se ainda houver saldo no mês`
                : ""}
              . O áudio é reaproveitado na publicação.
            </span>
          </span>
        </label>
      )}
      <fieldset className="rigor">
        <legend>Rigor dos quizzes e da avaliação final</legend>
        <div className="rigor-scale" role="radiogroup" aria-label="Rigor do aprendizado">
          {RIGOR_LEVELS.map((r) => (
            <label key={r} className={`rigor-dot${rigor === r ? " on" : ""}`}>
              <input type="radio" name="rigor" checked={rigor === r} onChange={() => setRigor(r)} />
              <span className="dot" aria-hidden="true" />
              <span className="rigor-label">{RIGOR_LABELS[r]}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <label className="field" style={{ marginTop: 14 }}>
        <span className="label">Orientação personalizada para a criação do curso (opcional)</span>
        <textarea
          rows={3}
          maxLength={GUIDANCE_MAX}
          value={guidance}
          placeholder="Ex.: use exemplos do varejo, linguagem direta e destaque os prazos legais."
          onChange={(e) => setGuidance(e.target.value)}
          aria-invalid={guidanceCheck.ok ? undefined : true}
        />
        <span className="segment-meta">
          Descreva foco, exemplos e linguagem. A orientação complementa as regras da plataforma, que mantêm o
          curso fiel ao ebook. {guidance.length}/{GUIDANCE_MAX}
        </span>
        {!guidanceCheck.ok && (
          <span className="error">Ajuste a orientação: ela {guidanceCheck.problems.join("; ")}.</span>
        )}
      </label>
      {simulated && (
        <p className="alert warn" style={{ marginTop: 12 }}>
          Nenhum provedor de IA configurado: a geração usará o simulador, sem custo, com conteúdo de
          demonstração.
        </p>
      )}
      {unpriced && (
        <p className="segment-meta" style={{ marginTop: 8 }}>
          Há modelos sem preço cadastrado; a estimativa considera apenas os modelos com preço conhecido.
        </p>
      )}
      {error && (
        <p className="alert error" style={{ marginTop: 12 }}>
          {error}
        </p>
      )}
      <div className="actions" style={{ marginTop: 16 }}>
        <button className="btn" disabled={busy || !guidanceCheck.ok}>
          {busy ? "Iniciando…" : status === "failed" ? "Retomar geração" : "Gerar curso"}
        </button>
      </div>
    </form>
  );
}

// ─────────────────────────── Acompanhamento ───────────────────────────

function StepIcon({ status }: { status: string }) {
  if (status === "done")
    return (
      <span className="step-icon done" aria-label="Concluída">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        >
          <path d="M5 12l5 5 9-10" />
        </svg>
      </span>
    );
  if (status === "running")
    return (
      <span className="step-icon running" aria-label="Em andamento">
        <span className="spinner" />
      </span>
    );
  if (status === "waiting_batch")
    return (
      <span className="step-icon waiting" aria-label="Aguardando lote">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      </span>
    );
  return <span className="step-icon" aria-label="Pendente" />;
}

const COUNT_LABELS: Array<[string, string]> = [
  ["modules", "Módulos"],
  ["lessons", "Aulas"],
  ["quizQuestions", "Questões de quiz"],
  ["flashcards", "Flashcards"],
  ["caseStudies", "Estudos de caso"],
  ["practicalExercises", "Exercícios práticos"],
  ["glossaryTerms", "Termos do glossário"],
  ["finalQuestions", "Questões da avaliação final"],
];

export function GenerationPage() {
  const { id = "" } = useParams();
  const [data, setData] = useState<Generation | null>(null);
  const [outline, setOutline] = useState<Outline | null>(null);
  const [error, setError] = useState("");
  const [retrying, setRetrying] = useState(false);
  const [stopping, setStopping] = useState(false);
  const fx = useFx();

  const load = useCallback(() => {
    api
      .get<Generation>(`/courses/${id}/generation`)
      .then((g) => {
        setData(g);
        if (g.generation?.status === "succeeded")
          api
            .get<Outline>(`/courses/${id}/outline`)
            .then(setOutline)
            .catch(() => undefined);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "Falha ao carregar."));
  }, [id]);

  useEffect(load, [load]);
  const status = data?.generation?.status;
  useEffect(() => {
    if (!status || !ACTIVE.has(status)) return;
    const t = setInterval(load, status === "waiting_batch" ? 15_000 : 2_000);
    return () => clearInterval(t);
  }, [status, load]);

  if (error)
    return (
      <main className="page">
        <p className="alert error">{error}</p>
      </main>
    );
  if (!data)
    return (
      <main className="page">
        <Spinner />
      </main>
    );

  const g = data.generation;
  const cur = data.current;
  // Progresso geral: etapas concluídas mais a fração da etapa atual (cada etapa pesa o mesmo).
  const stepList = data.steps ?? [];
  const doneSteps = stepList.filter((s) => s.status === "done").length;
  const stepFraction = cur?.total ? (cur.done ?? 0) / cur.total : 0;
  const pct = stepList.length ? Math.round(((doneSteps + stepFraction) / stepList.length) * 100) : 0;
  // Etapas de chamada única (estrutura, glossário, avaliação final) ficam com a barra em movimento.
  const singleCall = Boolean(cur && (cur.total ?? 0) <= 1 && status && ACTIVE.has(status));

  return (
    <main className="page">
      <div className="page-head">
        <div style={{ minWidth: 0 }}>
          <div className="eyebrow">
            <Link to="/professor/ebooks">Ebooks</Link> / Geração do curso
          </div>
          <h1>{data.course.title}</h1>
          <p>
            {data.version ? `Versão ${data.version.number}` : "Ainda sem geração"}
            {g?.mode ? ` · ${g.mode === "batch" ? "modo em lote" : "modo imediato"}` : ""}
          </p>
        </div>
        {g && (
          <span className={`badge ${status === "succeeded" ? "ok" : "warn"}`}>
            <span className={`dot${status && ACTIVE.has(status) ? " pulse" : ""}`} />
            {
              {
                queued: "Na fila",
                running: "Gerando",
                waiting_batch: "Aguardando lote",
                succeeded: "Concluído",
                failed: "Pausada",
              }[g.status]
            }
          </span>
        )}
      </div>

      {!g ? (
        <div className="card empty">
          <h2>Geração ainda não iniciada</h2>
          <p>Abra o ebook do curso e use o botão “Gerar curso”.</p>
        </div>
      ) : (
        <div className="gen-layout">
          <section className="card card-pad" aria-live="polite">
            <h2 style={{ fontSize: "1.1rem" }}>Etapas</h2>
            <ol className="steps">
              {data.steps?.map((s) => (
                <li key={s.id} className={`step ${s.status}`}>
                  <StepIcon status={s.status} />
                  <span className="step-label">{s.label}</span>
                  {s.status === "running" && cur?.step === s.label && cur.total && cur.total > 1 && (
                    <span className="segment-meta">
                      {cur.done} de {cur.total}
                    </span>
                  )}
                  {s.status === "waiting_batch" && <span className="segment-meta">no provedor de IA</span>}
                  {s.status === "done" && g.timings?.[s.id] && (
                    <span className="segment-meta">{span(g.timings[s.id]!.activeMs)}</span>
                  )}
                </li>
              ))}
            </ol>
            {status && ACTIVE.has(status) && (
              <>
                <div
                  className={`progress${singleCall ? " working" : ""}`}
                  style={{ marginTop: 16 }}
                  aria-hidden
                >
                  <span style={{ width: `${Math.max(3, pct)}%` }} />
                </div>
                <p className="segment-meta" style={{ marginTop: 8, marginBottom: 0 }}>
                  {status === "waiting_batch"
                    ? "O lote foi enviado ao provedor. Esta página pode ser fechada; a geração segue sozinha."
                    : isSlowGeneration(g)
                      ? "A geração está levando mais tempo do que o esperado. O book2class está tentando novamente, com outro modelo de IA se preciso, e retoma sozinho de onde parou. O que já ficou pronto está salvo e esta página pode ser fechada."
                      : status === "queued"
                        ? "Aguardando o processamento começar…"
                        : singleCall && cur?.step
                          ? `${cur.step}: a IA prepara esta etapa de uma só vez e pode levar alguns minutos. Esta página pode ser fechada.`
                          : "Gerando… esta página pode ser fechada; a geração segue sozinha."}
                </p>
                <button
                  type="button"
                  className="btn ghost small"
                  style={{ marginTop: 12 }}
                  disabled={stopping}
                  onClick={async () => {
                    if (
                      !window.confirm(
                        "Interromper a geração? O que já ficou pronto fica guardado, e você pode retomar depois de onde parou.",
                      )
                    )
                      return;
                    setStopping(true);
                    await api.post(`/courses/${id}/generation/cancel`, {}).catch(() => undefined);
                    setStopping(false);
                    load();
                  }}
                >
                  {stopping ? "Interrompendo…" : "Interromper a geração"}
                </button>
              </>
            )}
            {status === "failed" && (
              <div className="alert" style={{ marginTop: 16 }}>
                {g.error === GENERATION_CANCELLED_MESSAGE ? (
                  <p style={{ marginBottom: 10 }}>
                    <strong>Geração interrompida.</strong> O que já ficou pronto está salvo. Para continuar,
                    clique em “Retomar de onde parou”.
                  </p>
                ) : (
                  <>
                    <p style={{ marginBottom: 6 }}>
                      <strong>A geração precisa de uma nova retomada.</strong>{" "}
                      {explainGenerationStop(g.error)}
                    </p>
                    <p style={{ marginBottom: 6 }}>
                      O que já ficou pronto está salvo. Aguarde alguns minutos e clique em “Retomar de onde
                      parou”. Se a situação se repetir, o administrador pode indicar outro modelo de IA no
                      painel administrativo.
                    </p>
                  </>
                )}
                {g.error && g.error !== GENERATION_CANCELLED_MESSAGE && (
                  <details style={{ marginBottom: 10 }}>
                    <summary className="segment-meta">Detalhe técnico</summary>
                    <p className="segment-meta" style={{ marginTop: 6 }}>
                      {g.error}
                    </p>
                  </details>
                )}
                <button
                  className="btn small"
                  disabled={retrying}
                  onClick={async () => {
                    setRetrying(true);
                    await api
                      .post(`/courses/${id}/generate`, { mode: g.mode ?? "realtime" })
                      .catch(() => undefined);
                    setRetrying(false);
                    load();
                  }}
                >
                  Retomar de onde parou
                </button>
              </div>
            )}
          </section>

          <aside className="gen-side">
            <div className="card stat">
              <div className="k">Tempo de geração</div>
              <div className="v" style={{ fontSize: "1.1rem" }}>
                {span(g.activeMs ?? Object.values(g.timings ?? {}).reduce((n, t) => n + t.activeMs, 0))}
              </div>
              <div className="segment-meta">
                Início {clock(g.startedAt)}
                {g.status === "succeeded" && g.finishedAt ? ` · conclusão ${clock(g.finishedAt)}` : ""}
              </div>
            </div>
            <div className="card stat">
              <div className="k">Custo de IA até agora</div>
              <div className="v" style={{ fontSize: "1.1rem" }}>
                {money(data.usage?.costUsd ?? 0, fx)}
              </div>
              <div className="segment-meta">
                {int(data.usage?.calls ?? 0)} chamada(s) ·{" "}
                {int((data.usage?.inputTokens ?? 0) + (data.usage?.outputTokens ?? 0))} tokens
              </div>
            </div>
            <div className="card card-pad counts">
              {COUNT_LABELS.map(([k, label]) => (
                <div key={k} className="count-row">
                  <span>{label}</span>
                  <strong>{int(data.counts?.[k] ?? 0)}</strong>
                </div>
              ))}
            </div>
          </aside>
        </div>
      )}

      {status === "succeeded" && outline && (
        <section style={{ marginTop: 28 }}>
          <div className="page-head" style={{ marginBottom: 12 }}>
            <div>
              <h2 style={{ marginBottom: 4 }}>Sumário gerado</h2>
              <p>Revise, edite e publique o curso na área de trabalho.</p>
            </div>
            <Link className="btn" to={`/professor/cursos/${id}`}>
              Revisar e publicar
            </Link>
          </div>
          <CourseSearch courseId={id} />
          {outline.modules.map((m) => (
            <section key={m.id} className="card segment">
              <div className="segment-head">
                <h3>
                  Módulo {m.position}. {m.title}
                </h3>
                <span className="segment-meta">{m.lessons.length} aula(s)</span>
              </div>
              {m.summary && (
                <p className="segment-meta" style={{ marginTop: 6 }}>
                  {m.summary}
                </p>
              )}
              <ol className="lesson-list">
                {m.lessons.map((l) => (
                  <li key={l.id}>
                    <span>
                      {m.position}.{l.position} {l.title}
                    </span>
                    <span className="segment-meta">
                      {l.readingMinutes ? `${l.readingMinutes} min` : ""}
                      {l.sourcePageStart
                        ? ` · p. ${l.sourcePageStart}${l.sourcePageEnd && l.sourcePageEnd !== l.sourcePageStart ? `–${l.sourcePageEnd}` : ""}`
                        : ""}
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </section>
      )}
    </main>
  );
}

function CourseSearch({ courseId }: { courseId: string }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<SearchResult[] | null>(null);
  return (
    <form
      className="card card-pad search-box"
      role="search"
      onSubmit={async (e) => {
        e.preventDefault();
        const r = await api
          .get<{ results: SearchResult[] }>(`/courses/${courseId}/search?q=${encodeURIComponent(q)}`)
          .catch(() => ({ results: [] }));
        setResults(r.results);
      }}
    >
      <label htmlFor="course-search" className="label">
        Testar a busca interna do curso
      </label>
      <div className="search-row">
        <input
          id="course-search"
          type="text"
          placeholder="Ex.: fluxo de caixa"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button className="btn" disabled={q.trim().length < 2}>
          Buscar
        </button>
      </div>
      {results && (
        <ul className="search-results">
          {results.length === 0 && <li className="segment-meta">Nada encontrado.</li>}
          {results.map((r) => (
            <li key={r.id}>
              <strong>{r.title}</strong>
              <span className="segment-meta">
                {" "}
                · {r.kind === "glossary" ? "Glossário" : r.context}
                {r.pageStart ? ` · p. ${r.pageStart}` : ""}
              </span>
              <div className="snippet">{r.snippet}</div>
            </li>
          ))}
        </ul>
      )}
    </form>
  );
}
